import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { consumePostLoginPath } from "./utils/authSession";
import { useApp } from "./contexts/AppContext";

// Layouts
import ProtectedRoute from "./components/layout/ProtectedRoute";
import AppLayout from "./components/layout/AppLayout";
import RubricsLayout from "./components/layout/RubricsLayout";
import EvaluateLayout from "./components/layout/EvaluateLayout";
import PapersLayout from "./components/layout/PapersLayout";
import UsersLayout from "./components/layout/UsersLayout";

// Public pages
import Login from "./components/Login";
import LandingPage from "./components/LandingPage";

// Authenticated pages
import Dashboard from "./components/Dashboard";
import RubricChatbot from "./components/RubricChatbot";
import RubricUploader from "./components/RubricUploader";
import SavedRubrics from "./components/SavedRubrics";
import ScansTab from "./components/ScansTab";
import AnalyzeTab from "./components/AnalyzeTab";
import AssessmentTab from "./components/AssessmentTab";
import StudentUploader from "./components/StudentUploader";
import StudentsTab from "./components/StudentsTab";
import MistakesTab from "./components/MistakesTab";
import BlankPagesTab from "./components/BlankPagesTab";
import RechecksTab from "./components/RechecksTab";
import FeedbackTab from "./components/FeedbackTab";
import MessagesTab from "./components/MessagesTab";
import ModelRequestsTab from "./components/ModelRequestsTab";
import CreatePaper from "./components/CreatePaper";
import SavedPapers from "./components/SavedPapers";
import TablesTab from "./components/TablesTab";
import UserManagement from "./components/UserManagement";
import UserHistory from "./components/UserHistory";
import AccessManagement from "./components/AccessManagement";
import OCRTab from "./components/OCRTab";
import Chatbot from "./components/Chatbot";
import MCQsLayout from "./components/layout/MCQsLayout";
import MCQsOMR from "./components/MCQsOMR";
import MCQsFormatting from "./components/MCQsFormatting";
import BoardResultsTab from "./components/BoardResultsTab";
import PlansPreparationsTab from "./components/PlansPreparationsTab";
import FeesTab from "./components/FeesTab";
import InternetErrorBanner from "./components/InternetErrorBanner";
import PipelineDashboard from "./components/PipelineDashboard";
import WorkLogTab from "./components/WorkLogTab";

function RedirectAfterLogin() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(consumePostLoginPath(), { replace: true });
  }, [navigate]);
  return null;
}

export default function App() {
  const { isAuthenticated, isLoading, handleLogin, currentUser, hasAccess } = useAuth();
  const {
    tableData,
    rubricText,
    pdfRubric,
    recheckRequest,
    addCSVData,
    clearData,
    updateTableData,
    handleRecheckStudent,
    clearRecheckRequest
  } = useApp();

  // Don't render routes until auth state is restored
  if (isLoading) return <InternetErrorBanner />;

  return (
    <>
    <InternetErrorBanner />
    <Routes>
      {/* Public routes - redirect to dashboard if already logged in */}
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />}
      />
      <Route
        path="/login"
        element={isAuthenticated ? <RedirectAfterLogin /> : <Login onLogin={handleLogin} />}
      />

      {/* Authenticated routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          {/* Dashboard */}
          <Route path="/dashboard" element={<Dashboard />} />

          {/* Chatbot — visible to everyone */}
          <Route path="/chatbot" element={<Chatbot />} />

          {/* Rubrics (nested sub-tabs) — default sub-tab is role-based:
              teachers land on Generate (their primary workflow is building
              new rubrics), everyone else (examiners, admins) lands on
              Saved (they mostly review the existing library). */}
          <Route path="/rubrics" element={<RubricsLayout />}>
            <Route index element={<Navigate to={currentUser?.role === 'teacher' ? 'generate' : 'saved'} replace />} />
            <Route path="generate" element={<RubricChatbot />} />
            <Route path="edit" element={<RubricUploader />} />
            <Route path="saved" element={<SavedRubrics currentUser={currentUser} />} />
          </Route>

          {/* Scans */}
          <Route path="/scans" element={<ScansTab currentUser={currentUser} />} />

          {/* Evaluate (nested sub-tabs) */}
          <Route path="/evaluate" element={<EvaluateLayout />}>
            <Route index element={<Navigate to="sort" replace />} />
            <Route
              path="sort"
              element={<AnalyzeTab recheckRequest={recheckRequest} onRecheckComplete={clearRecheckRequest} />}
            />
            <Route
              path="check"
              element={
                <AssessmentTab
                  rubricText={rubricText}
                  pdfRubric={pdfRubric}
                  onAddResults={addCSVData}
                  onRecheckStudent={handleRecheckStudent}
                />
              }
            />
            <Route
              path="recheck"
              element={<StudentUploader onAddCSV={addCSVData} rubricText={rubricText} pdfRubric={pdfRubric} />}
            />
            <Route path="pipeline" element={
              hasAccess('pipeline')
                ? <PipelineDashboard />
                : <Navigate to="/dashboard" replace />
            } />
          </Route>

          {/* Students — teachers may view (read-only UI + API); others can edit */}
          <Route path="/students" element={
            <StudentsTab token={localStorage.getItem('authToken')} />
          } />

          {/* Board Results — management / examiner / administrator */}
          <Route path="/board-results" element={
            ['administrator', 'examiner', 'management'].includes(currentUser?.role)
              ? <BoardResultsTab />
              : <Navigate to="/dashboard" replace />
          } />

          {/* Fees — administrator / management (LAN-fast local DB) */}
          <Route path="/fees" element={
            hasAccess('fees')
              ? <FeesTab />
              : <Navigate to="/dashboard" replace />
          } />

          {/* Mistakes */}
          <Route path="/mistakes" element={<MistakesTab currentUser={currentUser} />} />

          {/* Unexpected blank pages — same folder record as Mistakes */}
          <Route path="/blank-pages" element={<BlankPagesTab currentUser={currentUser} />} />

          {/* Re-check requests (teachers submit; examiners/admins review) */}
          <Route path="/rechecks" element={<RechecksTab currentUser={currentUser} />} />

          {/* Feedback — annotated screenshots to show students their mistakes */}
          <Route path="/feedback" element={<FeedbackTab currentUser={currentUser} />} />

          {/* Messages */}
          <Route path="/messages" element={<MessagesTab currentUser={currentUser} />} />

          <Route path="/requests" element={
            hasAccess('requests')
              ? <ModelRequestsTab />
              : <Navigate to="/dashboard" replace />
          } />

          {/* Papers (nested sub-tabs) */}
          <Route path="/papers" element={<PapersLayout />}>
            <Route index element={<Navigate to="create" replace />} />
            <Route path="create" element={<CreatePaper currentUser={currentUser} />} />
            <Route path="saved" element={<SavedPapers currentUser={currentUser} />} />
          </Route>

          {/* Tables */}
          <Route
            path="/tables"
            element={<TablesTab data={tableData} onClear={clearData} onUpdateData={updateTableData} />}
          />

          {/* Admin */}
          <Route path="/users" element={<UsersLayout />}>
            <Route index element={<Navigate to="manage" replace />} />
            <Route path="manage" element={<UserManagement />} />
            <Route path="history" element={<UserHistory />} />
          </Route>
          <Route path="/access" element={<AccessManagement />} />
          <Route path="/courses" element={<Navigate to="/students" replace />} />

          {/* Plans & Prep — teachers + examiners + admins */}
          <Route path="/plans-preparations" element={
            hasAccess('plans-preparations')
              ? <PlansPreparationsTab />
              : <Navigate to="/dashboard" replace />
          } />

          {/* Old Curriculum UI removed — data/API kept for Plans & Prep */}
          <Route path="/curriculum/*" element={<Navigate to="/plans-preparations" replace />} />
          <Route path="/curriculum" element={<Navigate to="/plans-preparations" replace />} />
          <Route path="/work-log" element={
            hasAccess('work-log')
              ? <WorkLogTab />
              : <Navigate to="/dashboard" replace />
          } />
          <Route path="/ocr" element={
            currentUser?.role === 'teacher'
              ? <Navigate to="/dashboard" replace />
              : <OCRTab />
          } />

          {/* MCQs (nested sub-tabs) — not accessible to teachers */}
          <Route path="/mcqs" element={
            currentUser?.role === 'teacher'
              ? <Navigate to="/dashboard" replace />
              : <MCQsLayout />
          }>
            <Route index element={<Navigate to="omr" replace />} />
            <Route path="omr" element={<MCQsOMR />} />
            <Route path="approve" element={<Navigate to="/mcqs/omr" replace />} />
            <Route path="formatting" element={<MCQsFormatting />} />
          </Route>
          <Route path="/settings" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Route>

      {/* Fallback - redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
